import get from "lodash/get";
import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { BookingCategorySelector } from "PFApp/components/activity_form/field_factory/field_wrappers/booking_category_selector";
import {
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useBookingCategoryLink } from "PFApp/hooks/activity_form/use_booking_category_link";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { HintWrapper } from "./hint_wrapper";

export const BookingCategory = () => {
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);
  const template = useActivityFormStore((state) => state.template);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const selectedParentActivity = useWatch({
    name: FIELD_PARENT_ACTIVITY_KEY
  });
  const bookingCategoryId = useWatch({
    name: FIELD_BOOKING_CATEGORY_ID_KEY
  });

  const { getBookingCategoryLinkHelpers } = useBookingCategoryLink({
    template
  });
  const { canLink, isLinked, parentBookingCategoryId } = getBookingCategoryLinkHelpers({
    parentActivity: selectedParentActivity,
    activityCreator,
    subtemplate,
    bookingCategoryId
  });

  const required = isFieldRequired({ name: FIELD_BOOKING_CATEGORY_ID_KEY });

  const { title } = template.schema.properties.booking_category_id;

  return (
    <HintWrapper
      hint={t("sections.basicInformation.bookingCategory.addBookingCategoryDescription")}
      qaId={`${qaIdPrefix}-bookingCategory`}
    >
      <Controller
        name={FIELD_BOOKING_CATEGORY_ID_KEY}
        control={control}
        rules={{
          validate: (value: string) => {
            if (!submittedAsDraft && required && !value) {
              return t("errors.cantBeBlank");
            }
            return true;
          }
        }}
        render={({ field }) => (
          <BookingCategorySelector
            activityCreator={activityCreator}
            onChange={field.onChange}
            bookingCategoryId={field.value}
            template={template}
            subtemplate={subtemplate}
            error={get(errors, field.name)?.message as string}
            parentActivity={selectedParentActivity}
            onRestore={
              !isLinked && canLink
                ? () => setValue(FIELD_BOOKING_CATEGORY_ID_KEY, parentBookingCategoryId)
                : undefined
            }
            required={required}
            label={title ?? t("sections.basicInformation.bookingCategory.title")}
          />
        )}
      />
    </HintWrapper>
  );
};
